
/**
 * Nombre para mostrar/descargar cuando el API separa `Nombre` y `Extension`
 * (p. ej. detalle OCA: `Nombre` suele incluir ya la extensión extraída de la URL).
 */
export function formatDocumentFileNameWithExtension(
  nombre: string | null | undefined,
  extension: string | null | undefined,
  fallback = "documento",
): string {
  const base = decodeDocumentFileName(nombre);
  if (!base) {
    return fallback;
  }
  if (!extension?.trim()) {
    return base;
  }
  const ext = extension.trim().replace(/^\./, "");
  if (!ext) {
    return base;
  }
  const suffix = `.${ext.toLowerCase()}`;
  if (base.toLowerCase().endsWith(suffix)) {
    return base;
  }
  return `${base}.${ext}`;
}

/**
 * Decodifica nombres de fichero en percent-encoding (%20, %C3%B1, …).
 * Idempotente: si ya está decodificado, devuelve el mismo texto salvo '+' → espacio.
 */
export function decodeDocumentFileName(fileName: string | null | undefined): string {
  if (fileName == null) {
    return "";
  }

  let name = fileName.trim();
  if (name === "") {
    return name;
  }

  if (name.startsWith('"') && name.endsWith('"') && name.length >= 2) {
    name = name.slice(1, -1);
  }

  const withSpaces = name.replace(/\+/g, " ");

  if (!/%[0-9A-Fa-f]{2}/.test(withSpaces)) {
    return withSpaces;
  }

  try {
    return decodeURIComponent(withSpaces);
  } catch {
    return withSpaces;
  }
}

/**
 * Extrae y decodifica el nombre desde Content-Disposition (filename / filename* RFC 5987).
 */
export function parseContentDispositionFileName(
  contentDisposition: string | null | undefined,
): string | null {
  if (contentDisposition == null || contentDisposition.trim() === "") {
    return null;
  }

  const header = contentDisposition.trim();

  const filenameStar = /filename\*=(?:UTF-8''|utf-8'')([^;\r\n]+)/i.exec(header);
  if (filenameStar?.[1]) {
    return decodeDocumentFileName(filenameStar[1].trim());
  }

  const quoted = /filename\*?=(["'])(.*?)\1/i.exec(header);
  if (quoted?.[2]) {
    return decodeDocumentFileName(quoted[2]);
  }

  const unquoted = /filename\*?=([^;\r\n]+)/i.exec(header);
  if (unquoted?.[1]) {
    return decodeDocumentFileName(unquoted[1].trim());
  }

  return null;
}

const EXTENSION_TO_MIME: Record<string, string> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

/** MIME para visualización (iframe / blob URL); evita octet-stream en PDFs. */
export function resolveDocumentMimeType(contentTypeHeader: string, fileName: string): string {
  const fromHeader = contentTypeHeader.split(";")[0]?.trim() ?? "";
  if (fromHeader && fromHeader !== "application/octet-stream") {
    return fromHeader;
  }

  const dot = fileName.lastIndexOf(".");
  const ext = dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : "";
  return EXTENSION_TO_MIME[ext] ?? fromHeader ?? "application/octet-stream";
}

export function toDisplayableFile(file: File): File {
  const mime = resolveDocumentMimeType(file.type, file.name);
  if (file.type === mime) {
    return file;
  }
  return new File([file], file.name, { type: mime, lastModified: file.lastModified });
}

export function blobPartFromResponseData(datos: unknown): BlobPart {
  if (typeof Blob !== "undefined" && datos instanceof Blob) {
    return datos;
  }
  if (typeof ArrayBuffer !== "undefined" && datos instanceof ArrayBuffer) {
    return datos;
  }
  if (typeof datos === "string") {
    const buf = new Uint8Array(datos.length);
    for (let i = 0; i < datos.length; i++) {
      buf[i] = datos.charCodeAt(i) & 0xff;
    }
    return buf;
  }
  return datos as BlobPart;
}
